import { useState, useEffect } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import { getProtectorInstallmentConfrontations } from '../../services/GuaranteesService';

interface InstallmentConfrontation {
  id: number;
  confrontation_date: string;
  global_id: number;
  monetization_type_id: number;
  monetization_type_name: string;
  installment_id: number;
  installment_number: number;
  protector_id: number;
  protector_name: string;
  percentage: number;
  amount: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface ProtectorDetailsDefaultSectionProps {
  protectorId?: string;
}

const ProtectorDetailsDefaultSection = ({ 
  protectorId
}: ProtectorDetailsDefaultSectionProps) => {
  const [confrontations, setConfrontations] = useState<InstallmentConfrontation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalElements: 0, totalPages: 0 });

  useEffect(() => {
    if (protectorId) {
      fetchConfrontations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protectorId, pagination.page]);

  const fetchConfrontations = async () => {
    if (!protectorId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getProtectorInstallmentConfrontations(protectorId, pagination.page, pagination.size);
      if (response.success && 'data' in response) {
        setConfrontations(response.data.content || []);
        setPagination({
          page: response.data.page || 0,
          size: response.data.size || 10,
          totalElements: response.data.total_elements || 0,
          totalPages: response.data.total_pages || 0
        });
      } else {
        setError('Error al cargar los instrumentos afrontados');
      }
    } catch (err) {
      setError('Error al cargar los instrumentos afrontados');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount: number) => {
    if (amount === null || amount === undefined) {
      return '0';
    }
    if (Number.isInteger(amount)) {
      return amount.toLocaleString('es-AR');
    }
    return amount.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatPercentage = (percentage: number) => {
    if (percentage === null || percentage === undefined) {
      return '0%';
    }
    return `${percentage.toLocaleString('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}%`;
  };

  const columns = [
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Aval</P>,
      selector: (row: InstallmentConfrontation) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.global_id || '-'}
        </P>
      ),
      minWidth: '100px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Tipo</P>,
      selector: (row: InstallmentConfrontation) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.monetization_type_name || '-'}
        </P>
      ),
      minWidth: '150px',
      grow: 1.5
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Nº Cuota</P>,
      selector: (row: InstallmentConfrontation) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {row.installment_number || '-'}
        </P>
      ),
      minWidth: '100px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Monto</P>,
      selector: (row: InstallmentConfrontation) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          $ {formatAmount(row.amount)}
        </P>
      ),
      minWidth: '120px',
      grow: 1
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>Fecha de confrontación</P>,
      selector: (row: InstallmentConfrontation) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatDate(row.confrontation_date)}
        </P>
      ),
      minWidth: '180px',
      grow: 1.5
    },
    {
      name: <P variant='secondary-2' type="3" weight="bold" withMargin={false}>% de aporte</P>,
      selector: (row: InstallmentConfrontation) => (
        <P variant='secondary-2' type="3" weight="regular" withMargin={false}>
          {formatPercentage(row.percentage)}
        </P>
      ),
      minWidth: '120px',
      grow: 1
    }
  ];

  return (
    <>
      <Card display='flex' justifyContent='space-between' alignItems='center' margin={{ top: '2', bottom: '3' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          Instrumentos afrontados
        </H>
      </Card>
      
      {loading ? (
        <Card>
          <TableSkeleton rows={5} columns={6} />
        </Card>
      ) : error ? (
        <Card>
          <P variant='neutral' modifier='weak' withMargin={false}>
            {error}
          </P>
        </Card>
      ) : confrontations && confrontations.length > 0 ? (
        <Card>
          <Table
            columns={columns}
            data={confrontations}
            tableData={pagination}
            handleChangePage={handleChangePage}
          />
        </Card>
      ) : (
        <Card>
          <P variant='neutral' modifier='weak' withMargin={false}>
            No hay instrumentos afrontados para mostrar.
          </P>
        </Card>
      )}
    </>
  );
};

export default ProtectorDetailsDefaultSection;
